import { CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { issueCategoryApi } from "api/issueCategory.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { ActionButton, INA, TableLayout, TableLayoutFooter, getTableShowingRange } from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IssueCategory } from "types/issueCategory";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { IssueCategoryModal } from "views/IssueCategory/components/IssueCategoryModal";

interface NewsQuery extends QueryParam {}

export const IssueCategoryPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<NewsQuery>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IssueCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedIssueCategory, setSelectedIssueCategory] = useState<Partial<IssueCategory>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  const debounceSearch = useCallback(
    debounce((keyword: string) => setQuery({ ...query, search: keyword, page: 1, limit: 20 }), 300),
    [setQuery, query],
  );

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await issueCategoryApi.findAll(query);
    setLoading(false);
    setData(res.data.issueCategories);
    setTotal(res.data.total);
  };

  const handleDelete = async (issueCategory: IssueCategory) => {
    setLoading(true);
    try {
      await issueCategoryApi.delete(issueCategory.id);
      fetchData();
      message.info("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo<TableColumnsType<IssueCategory>>(
    () => [
      {
        title: "STT",
        key: "stt",
        width: 80,
        render: (_, __, index) => <span>{(query.page - 1) * query.limit + index + 1}</span>,
      },
      {
        title: "Loại sự cố",
        dataIndex: "name",
        key: "name",
        width: 120,
        render: (_, record) => <span>{record.name}</span>,
      },
      {
        title: "Mô tả",
        dataIndex: "description",
        key: "description",
        width: 140,
        render: (_, record) => <span>{record.description}</span>,
      },
      {
        title: "Thao tác",
        key: "action",
        width: 120,
        align: "center",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  setSelectedIssueCategory(record);
                  setVisibleModal(true);
                  setModalStatus("update");
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Xóa">
              <Popconfirm
                placement="top"
                title="Xác nhận xóa danh mục này?"
                onConfirm={() => handleDelete(record)}
                okText="Xóa"
                cancelText="Không"
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>
          </div>
        ),
      },
    ],
    [query.limit, query.page],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                tone="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedIssueCategory({});
                }}
              >
                Thêm mới
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<IssueCategory>
            loading={loading}
            pagination={false}
            rowKey="id"
            dataSource={data}
            columns={columns}
            scroll={{ x: 400 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <IssueCategoryModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        issueCategory={selectedIssueCategory}
        status={modalStatus}
      />
    </div>
  );
});
